import { useEffect, useState } from 'react';

/**
 * YouTube-style ambient mode via real pixel extraction.
 *
 * Each tick (4 Hz while playing) we draw the video onto a hidden 32×32
 * canvas and read it back with `getImageData`. Pixel RGB values are summed
 * with an edge-proximity weight so a bright object in the middle of the
 * frame doesn't dominate the result — the dominant *background* color wins.
 *
 * Returns:
 *   - color   — current `rgb(R, G, B)` string, or null when disabled / not
 *               yet sampled.
 *   - tainted — true when canvas reads are blocked (cross-origin source
 *               without proper CORS). Caller should treat this as "ambient
 *               unavailable" and stop rendering the wash.
 *
 * Requires the `<video>` element to have `crossOrigin="anonymous"` AND the
 * server to return `Access-Control-Allow-Origin` for canvas reads to
 * succeed. Without those, `getImageData` throws SecurityError once and the
 * hook gives up.
 */
export function useAmbientColor(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  enabled: boolean,
): { color: string | null; tainted: boolean } {
  const [color, setColor] = useState<string | null>(null);
  const [tainted, setTainted] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setColor(null);
      return;
    }
    const video = videoRef.current;
    if (!video) return;

    const W = 32;
    const H = 32;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    // Precompute per-pixel weights: distance from center, normalised, then
    // squared so edges win decisively over the center. The squared term is
    // what makes the result track *background* tones rather than the focal
    // subject — bright centred objects barely register.
    const cx = (W - 1) / 2;
    const cy = (H - 1) / 2;
    const maxDist = Math.sqrt(cx * cx + cy * cy);
    const weights = new Float32Array(W * H);
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const d = Math.hypot(x - cx, y - cy);
        weights[y * W + x] = (d / maxDist) ** 2;
      }
    }

    let stopped = false;
    let intervalId = 0;

    const sample = () => {
      if (stopped || document.hidden) return;
      try {
        ctx.drawImage(video, 0, 0, W, H);
        const data = ctx.getImageData(0, 0, W, H).data;
        let r = 0;
        let g = 0;
        let b = 0;
        let total = 0;
        for (let i = 0; i < weights.length; i++) {
          const w = weights[i];
          const off = i * 4;
          r += data[off] * w;
          g += data[off + 1] * w;
          b += data[off + 2] * w;
          total += w;
        }
        if (total > 0) {
          setColor(`rgb(${Math.round(r / total)}, ${Math.round(g / total)}, ${Math.round(b / total)})`);
        }
      } catch (err) {
        if (err instanceof DOMException && (err.name === 'SecurityError' || err.name === 'InvalidStateError')) {
          setTainted(true);
          stopped = true;
        }
      }
    };

    const start = () => {
      window.clearInterval(intervalId);
      sample();
      intervalId = window.setInterval(sample, 250);
    };
    const stop = () => window.clearInterval(intervalId);

    const onPlay = () => start();
    const onPause = () => stop();
    const onSeeked = () => sample();
    const onLoaded = () => sample();
    const onVisibility = () => {
      if (document.hidden) stop();
      else if (!video.paused) start();
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onPause);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('loadeddata', onLoaded);
    document.addEventListener('visibilitychange', onVisibility);

    if (!video.paused && !video.ended) start();
    else if (video.readyState >= 2) sample();

    return () => {
      stopped = true;
      stop();
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onPause);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('loadeddata', onLoaded);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, videoRef]);

  return { color, tainted };
}
